import { accountIsActive, findMasterDataRecord, primaryPlatformLinks } from './snapshot.js';
import { EMPTY_VALIDATION_CONFIGURATION, INITIATIVE_FIELD_PATHS, MAX_RULE_MESSAGE_LENGTH, MIN_RULE_MESSAGE_LENGTH, R8_10_GOVERNANCE_RULE_IDS, R8_3_HARD_RULE_IDS, R8_6_WARNING_RULE_IDS, R8_7_INFORMATION_RULE_IDS, VALIDATION_RULE_IDS, effectiveRuleSettings, isValidRuleMessage, } from './validation-rule.js';
import { witnessHashesOf } from './witness-hash.js';
import { deriveFieldVisibility, isFieldVisible } from './field-visibility.js';
import { PLATFORM_SET_MESSAGE_KEYS, PLATFORM_SET_STRUCTURE_RULE_ID, PLATFORM_SPLIT_RULE_ID, validatePlatformSet, } from './platform-set.js';
import { dateInterval, dateIntervalsIntersect } from '../salescycle/date-interval.js';
/** Longest Title R4.2, R5.6 and the hard rule of R8.3 accept (assumption A16). */
export const MAX_INITIATIVE_TITLE_LENGTH = 200;
/**
 * The capability the Authorization_Service grants a user permitted to change
 * confidentiality (R3.8, R3.9). Named so `HV-006` reads as the criterion does.
 */
const CONFIDENTIALITY_CAPABILITY = 'initiative:confidentiality';
/** No finding: the rule is met. Named so a rule body reads as a verdict. */
const MET = null;
/**
 * Whether stored text counts as present.
 *
 * Whitespace is not a value. Every nullable text column carries
 * `CHECK (col <> '')` so `null` is the one representation of unset, but a column
 * holding `'   '` passes that check and would satisfy a naive emptiness test
 * while showing the user a blank field. R9.6's "non-blank" wording is the same
 * reading applied to a reason.
 */
function isPresent(text) {
    return text !== null && text.trim() !== '';
}
/** The Initiative's own date range as a closed interval. */
function initiativeInterval(snapshot) {
    return dateInterval(snapshot.initiative.startDate, snapshot.initiative.endDate);
}
/** Every controlled-value field of the aggregate, with its master data population. */
function controlledValueFields(snapshot) {
    const { initiative, masterData } = snapshot;
    return [
        {
            field: INITIATIVE_FIELD_PATHS.platforms,
            ids: initiative.platforms.map((link) => link.platformId),
            records: masterData.platforms,
        },
        {
            field: INITIATIVE_FIELD_PATHS.channelIds,
            ids: initiative.channelIds,
            records: masterData.channels,
        },
        {
            field: INITIATIVE_FIELD_PATHS.customerGroupIds,
            ids: initiative.customerGroupIds,
            records: masterData.customerGroups,
        },
        {
            field: INITIATIVE_FIELD_PATHS.audienceIds,
            ids: initiative.audienceIds,
            records: masterData.audiences,
        },
        {
            field: INITIATIVE_FIELD_PATHS.categoryIds,
            ids: initiative.categoryIds,
            records: masterData.categories,
        },
        {
            field: INITIATIVE_FIELD_PATHS.initiativeTypeIds,
            ids: initiative.initiativeTypeIds,
            records: masterData.initiativeTypes,
        },
        {
            // Hidden, and so not evaluated, while CSC is not involved (R5.3).
            field: INITIATIVE_FIELD_PATHS.cscAttributeIds,
            ids: initiative.cscInvolvement ? (initiative.cscAttributeIds ?? []) : [],
            records: masterData.cscAttributes ?? [],
        },
        {
            field: INITIATIVE_FIELD_PATHS.priorityId,
            ids: [initiative.priorityId],
            records: masterData.priorities,
        },
        {
            field: INITIATIVE_FIELD_PATHS.milestones,
            ids: initiative.milestones.map((milestone) => milestone.milestoneTypeId),
            records: masterData.milestoneTypes,
        },
        {
            field: INITIATIVE_FIELD_PATHS.fieldForceScope,
            ids: initiative.fieldForceScope.map((entry) => entry.activityTypeId),
            records: masterData.fieldForceActivityTypes,
        },
    ];
}
/** The earliest recorded date among the milestones of a type, or `undefined`. */
function earliestRecordedDate(snapshot, milestoneTypeId) {
    const dates = snapshot.initiative.milestones
        .filter((milestone) => milestone.milestoneTypeId === milestoneTypeId)
        .map((milestone) => milestone.plannedDate);
    // `PlainDate` is fixed-width and zero-padded, so string order is calendar
    // order and the minimum needs no comparator (see `src/date/plain-date.ts`).
    return dates.length === 0 ? undefined : dates.reduce((a, b) => (a <= b ? a : b));
}
/**
 * A Platform set violation's own payload as finding detail: everything the
 * violation carries except the identity it is being reported under.
 *
 * `ruleId` and `fields` are dropped because the finding already carries them;
 * `kind` and whatever the kind adds - the entered total and affected Platforms of
 * a split mismatch (R4.5), the count and maximum of an over-large set - are what
 * the message needs to name.
 */
function platformSetDetail(violation) {
    const detail = {};
    for (const [key, value] of Object.entries(violation)) {
        if (key !== 'ruleId' && key !== 'fields') {
            detail[key] = value;
        }
    }
    return detail;
}
/**
 * The findings of one rule identifier, drawn from a single Platform set verdict.
 *
 * `validatePlatformSet` already reports *every* violation of a submitted set and
 * already files each under `HV-005` or `HV-007`, so the two rules read the same
 * verdict and each takes its own violations. Re-implementing either rule here
 * would give the engine and the deferred constraint trigger a third opinion to
 * disagree with.
 */
function platformSetFindings(snapshot, ruleId) {
    const verdict = validatePlatformSet(snapshot.initiative.platforms);
    if (verdict.valid) {
        return MET;
    }
    const mine = verdict.violations.filter((violation) => violation.ruleId === ruleId);
    if (mine.length === 0) {
        return MET;
    }
    return mine.map((violation) => ({
        messageKey: PLATFORM_SET_MESSAGE_KEYS[violation.kind],
        fields: violation.fields,
        detail: platformSetDetail(violation),
    }));
}
const VALIDATION_RULE_TABLE = {
    // ---------------------------------------------------------------- hard (R8.3)
    /** Title empty or longer than 200 characters (R8.3, R4.2, A16). */
    'HV-001': {
        id: 'HV-001',
        declaredTier: 'hard',
        configurable: false,
        messageKey: 'validation.title.length',
        fields: [INITIATIVE_FIELD_PATHS.title],
        evaluate: (snapshot) => {
            const { title } = snapshot.initiative;
            if (isPresent(title) && title.length <= MAX_INITIATIVE_TITLE_LENGTH) {
                return MET;
            }
            return [
                {
                    detail: {
                        titleLength: title.length,
                        minimumLength: 1,
                        maximumLength: MAX_INITIATIVE_TITLE_LENGTH,
                    },
                },
            ];
        },
    },
    /**
     * Missing Owner (R8.3).
     *
     * Only presence. Whether the named account is active is R4.11's governance
     * finding (`GV-007`), and whether the Backup Owner differs from the Owner is
     * R4.10's own rule, which this task does not register.
     */
    'HV-002': {
        id: 'HV-002',
        declaredTier: 'hard',
        configurable: false,
        messageKey: 'validation.owner.missing',
        fields: [INITIATIVE_FIELD_PATHS.ownerUserId],
        evaluate: (snapshot) => (isPresent(snapshot.initiative.ownerUserId) ? MET : [{}]),
    },
    /**
     * End date earlier than start date (R8.3).
     *
     * `end === start` is legal: R8.3 blocks an end date *earlier* than the start,
     * and a one-day Initiative is a well-formed closed interval.
     */
    'HV-003': {
        id: 'HV-003',
        declaredTier: 'hard',
        configurable: false,
        messageKey: 'validation.dateRange.endBeforeStart',
        fields: [INITIATIVE_FIELD_PATHS.startDate, INITIATIVE_FIELD_PATHS.endDate],
        evaluate: (snapshot) => {
            const { startDate, endDate } = snapshot.initiative;
            if (endDate >= startDate) {
                return MET;
            }
            return [{ detail: { startDate, endDate } }];
        },
    },
    /**
     * A controlled-value field holding an inactive master data value (R8.3).
     *
     * A reference the master data load did not return at all is reported too,
     * under its own message key. `findMasterDataRecord` returns `undefined` for
     * one by design rather than throwing, and treating it as met would let a
     * dangling reference save.
     *
     * One finding per offending reference, so the validation summary of R5.7 can
     * name each value rather than only the field. R5.9's inactive marker is a
     * display affordance on the intake form and does not soften this rule: R8.3
     * blocks the save either way.
     */
    'HV-004': {
        id: 'HV-004',
        declaredTier: 'hard',
        configurable: false,
        messageKey: 'validation.controlledValue.inactive',
        fields: [
            INITIATIVE_FIELD_PATHS.platforms,
            INITIATIVE_FIELD_PATHS.channelIds,
            INITIATIVE_FIELD_PATHS.customerGroupIds,
            INITIATIVE_FIELD_PATHS.audienceIds,
            INITIATIVE_FIELD_PATHS.categoryIds,
            INITIATIVE_FIELD_PATHS.initiativeTypeIds,
            INITIATIVE_FIELD_PATHS.cscAttributeIds,
            INITIATIVE_FIELD_PATHS.priorityId,
            INITIATIVE_FIELD_PATHS.milestones,
            INITIATIVE_FIELD_PATHS.fieldForceScope,
        ],
        evaluate: (snapshot) => {
            const findings = [];
            for (const { field, ids, records } of controlledValueFields(snapshot)) {
                for (const id of [...new Set(ids)]) {
                    const record = findMasterDataRecord(records, id);
                    if (record === undefined) {
                        findings.push({
                            messageKey: 'validation.controlledValue.unknown',
                            fields: [field],
                            detail: { field, valueId: id },
                        });
                        continue;
                    }
                    if (!record.isActive) {
                        findings.push({
                            fields: [field],
                            detail: { field, valueId: id, valueCode: record.code },
                        });
                    }
                }
            }
            return findings.length === 0 ? MET : findings;
        },
    },
    /**
     * An entered Platform percentage split that does not total 100 percent (R8.3,
     * R4.5).
     *
     * Delegates to `validatePlatformSet`, which already reports every split
     * violation of a submitted set rather than the first, and keeps the split
     * findings apart from the structural ones by rule identifier. The detail of a
     * total mismatch therefore carries the entered total and the affected
     * Platforms, which is what R4.5 requires to be reported.
     */
    'HV-005': {
        id: 'HV-005',
        declaredTier: 'hard',
        configurable: false,
        messageKey: PLATFORM_SET_MESSAGE_KEYS.split_total_mismatch,
        fields: [INITIATIVE_FIELD_PATHS.platformSplit],
        evaluate: (snapshot) => platformSetFindings(snapshot, PLATFORM_SPLIT_RULE_ID),
    },
    /**
     * A confidentiality change requested by a user the Authorization_Service does
     * not permit to change confidentiality (R8.3, R3.8, R3.9).
     *
     * About the change, not the state: saving an already-confidential Initiative
     * without touching its confidentiality is not a violation, which is why the
     * request's intent arrives on the context rather than being inferred from the
     * snapshot.
     */
    'HV-006': {
        id: 'HV-006',
        declaredTier: 'hard',
        configurable: false,
        messageKey: 'validation.confidentiality.changeNotPermitted',
        fields: [INITIATIVE_FIELD_PATHS.isConfidential],
        evaluate: (_snapshot, context) => {
            if (!context.confidentialityChangeRequested) {
                return MET;
            }
            if (context.principal.capabilities.has(CONFIDENTIALITY_CAPABILITY)) {
                return MET;
            }
            return [{ detail: { requiredCapability: CONFIDENTIALITY_CAPABILITY } }];
        },
    },
    /**
     * The Platform set structure rules of R4.4: exactly one primary Platform, at
     * most nine additional ones, each Platform at most once.
     *
     * Hard because a set breaking them cannot be stored - a unique partial index
     * and a deferred constraint trigger say so - but not one of the six R8.3
     * enumerates. The default message key names the family; each finding narrows
     * it to the breakage it found, because R4.4 has four distinguishable ones and
     * a message about cardinality should not read like a message about a total.
     */
    'HV-007': {
        id: 'HV-007',
        declaredTier: 'hard',
        configurable: false,
        messageKey: 'validation.platformSet.invalid',
        fields: [INITIATIVE_FIELD_PATHS.platforms],
        evaluate: (snapshot) => platformSetFindings(snapshot, PLATFORM_SET_STRUCTURE_RULE_ID),
    },
    // --------------------------------------------------- governance (R8.10, R4.11)
    /**
     * The Initiative date range intersects no active Sales_Cycle (R8.10, R7.9).
     *
     * R7.9 requires the finding to name the Initiative date range, so the detail
     * carries both bounds. Inactive definitions are not candidates: R7.1 derives
     * from active cycles only, and a finding that counted an inactive one would
     * disagree with the derived set shown beside it.
     */
    'GV-001': {
        id: 'GV-001',
        declaredTier: 'governance',
        configurable: true,
        messageKey: 'validation.salesCycle.noIntersectingCycle',
        fields: [
            INITIATIVE_FIELD_PATHS.startDate,
            INITIATIVE_FIELD_PATHS.endDate,
            INITIATIVE_FIELD_PATHS.salesCycleIds,
        ],
        evaluate: (snapshot) => {
            const range = initiativeInterval(snapshot);
            const intersecting = snapshot.salesCycles.filter((cycle) => cycle.isActive &&
                dateIntervalsIntersect(range, dateInterval(cycle.startDate, cycle.endDate)));
            if (intersecting.length > 0) {
                return MET;
            }
            return [{ detail: { startDate: range.start, endDate: range.end } }];
        },
    },
    /** The Initiative holds no primary Platform (R8.10). */
    'GV-002': {
        id: 'GV-002',
        declaredTier: 'governance',
        configurable: true,
        messageKey: 'validation.platform.primaryMissing',
        fields: [INITIATIVE_FIELD_PATHS.platforms],
        evaluate: (snapshot) => primaryPlatformLinks(snapshot.initiative.platforms).length === 0 ? [{}] : MET,
    },
    /**
     * The Initiative holds no Channel and no audience association (R8.10).
     *
     * A conjunction, exactly as the criterion states it: an Initiative with
     * Channels but no audience is not caught here, and neither is the mirror
     * case.
     */
    'GV-003': {
        id: 'GV-003',
        declaredTier: 'governance',
        configurable: true,
        messageKey: 'validation.audience.channelAndAudienceMissing',
        fields: [INITIATIVE_FIELD_PATHS.channelIds, INITIATIVE_FIELD_PATHS.audienceIds],
        evaluate: (snapshot) => {
            const { channelIds, audienceIds } = snapshot.initiative;
            return channelIds.length === 0 && audienceIds.length === 0 ? [{}] : MET;
        },
    },
    /**
     * A milestone type configured as mandatory holds no recorded date (R8.10).
     *
     * One finding per missing type, so the summary names which milestone is
     * missing rather than only that one is. Inactive mandatory types are skipped:
     * a type an administrator has retired must not block records that never
     * carried it.
     */
    'GV-004': {
        id: 'GV-004',
        declaredTier: 'governance',
        configurable: true,
        messageKey: 'validation.milestone.mandatoryDateMissing',
        fields: [INITIATIVE_FIELD_PATHS.milestones],
        evaluate: (snapshot) => {
            const findings = [];
            for (const type of snapshot.masterData.milestoneTypes) {
                if (!type.isMandatory || !type.isActive) {
                    continue;
                }
                if (earliestRecordedDate(snapshot, type.id) === undefined) {
                    findings.push({
                        detail: { milestoneTypeId: type.id, milestoneTypeCode: type.code },
                    });
                }
            }
            return findings.length === 0 ? MET : findings;
        },
    },
    /**
     * Field Force involvement is set to yes and the Initiative holds zero Field
     * Force scope entries (R8.10).
     */
    'GV-005': {
        id: 'GV-005',
        declaredTier: 'governance',
        configurable: true,
        messageKey: 'validation.fieldForce.scopeMissing',
        fields: [INITIATIVE_FIELD_PATHS.fieldForceScope],
        evaluate: (snapshot) => {
            const { fieldForceInvolvement, fieldForceScope } = snapshot.initiative;
            return fieldForceInvolvement && fieldForceScope.length === 0 ? [{}] : MET;
        },
    },
    // `GV-006` (a DTE or CSC dependency entry without an owner or due date) is
    // retired: the dependency owner and due date are optional fields.
    /**
     * An assigned Owner or Backup Owner the Identity_Service reports as inactive
     * (R4.11).
     *
     * Not one of R8.10's six; R4.11 states it separately and requires exactly a
     * governance finding. The criterion also exempts the transition to Cancelled
     * from the block, which is the gate's business (task 5.5) and not a reason for
     * the rule to withhold the finding: a finding withheld from the gate cannot
     * be shown in the validation summary R4.11 also requires.
     *
     * An account the snapshot carries no status for is unknown to the
     * Identity_Service. R4.10 treats unknown like inactive but reports it
     * differently, so the two get separate message keys.
     */
    'GV-007': {
        id: 'GV-007',
        declaredTier: 'governance',
        configurable: true,
        messageKey: 'validation.owner.inactive',
        fields: [INITIATIVE_FIELD_PATHS.ownerUserId, INITIATIVE_FIELD_PATHS.backupOwnerUserId],
        evaluate: (snapshot) => {
            const { ownerUserId, backupOwnerUserId } = snapshot.initiative;
            const findings = [];
            const check = (userId, field, inactiveKey, unknownKey) => {
                const active = accountIsActive(snapshot.accounts, userId);
                if (active === true) {
                    return;
                }
                findings.push({
                    messageKey: active === undefined ? unknownKey : inactiveKey,
                    fields: [field],
                    detail: { field, userId },
                });
            };
            if (isPresent(ownerUserId)) {
                check(ownerUserId, INITIATIVE_FIELD_PATHS.ownerUserId, 'validation.owner.inactive', 'validation.owner.unknown');
            }
            if (backupOwnerUserId !== null && isPresent(backupOwnerUserId)) {
                check(backupOwnerUserId, INITIATIVE_FIELD_PATHS.backupOwnerUserId, 'validation.owner.backupInactive', 'validation.owner.backupUnknown');
            }
            return findings.length === 0 ? MET : findings;
        },
    },
    // ------------------------------------------------------------- warning (R8.6)
    /**
     * The Initiative date range overlaps by at least one day the date range of
     * another active Initiative that holds the same primary Platform and at least
     * one common audience (R8.6).
     *
     * The candidates arrive on the context from the single indexed query
     * design.md permits; the three clauses are checked here, so a query that
     * over-returns raises no warning and the rule states R8.6 in full. One
     * finding per overlapping Initiative, naming it and the shared audiences.
     *
     * An Initiative never overlaps itself: a candidate carrying the snapshot's own
     * identifier is skipped, so re-saving a record does not warn about it.
     */
    'WV-001': {
        id: 'WV-001',
        declaredTier: 'warning',
        configurable: true,
        messageKey: 'validation.overlap.sharedPlatformAndAudience',
        fields: [
            INITIATIVE_FIELD_PATHS.startDate,
            INITIATIVE_FIELD_PATHS.endDate,
            INITIATIVE_FIELD_PATHS.platforms,
            INITIATIVE_FIELD_PATHS.audienceIds,
        ],
        /**
         * The acknowledgement expiry basis of R8.11: exactly the Initiative values
         * the three clauses of R8.6 read - the date range, the primary Platform and
         * the audience set.
         *
         * The primary Platform arrives as a *set*, not as "the" primary: a set with
         * no primary and one with two are both states `evaluate` declines to warn
         * about, and collapsing them to one `null` would let an acknowledgement
         * survive the change between them. The audience identifiers are a set too,
         * because R8.6 asks for "at least one common audience" and their order is not
         * a value the user chose - reordering the list must not expire an
         * acknowledgement, and the sorted canonical form is what makes that true.
         *
         * Not in the witness: `context.overlapCandidates`. The witness signature is a
         * function of the snapshot alone, and R8.11 speaks of the user's own "later
         * edit that changes a field value". A *different* Initiative moving into or
         * out of the range is therefore not an expiry event; it changes which
         * findings the rule raises, and R8.5 already requires every warning of that
         * request to be acknowledged.
         */
        witness: (snapshot) => ({
            startDate: snapshot.initiative.startDate,
            endDate: snapshot.initiative.endDate,
            primaryPlatformIds: new Set(primaryPlatformLinks(snapshot.initiative.platforms).map((link) => link.platformId)),
            audienceIds: new Set(snapshot.initiative.audienceIds),
        }),
        evaluate: (snapshot, context) => {
            const primaries = primaryPlatformLinks(snapshot.initiative.platforms);
            const primary = primaries.length === 1 ? primaries[0] : undefined;
            if (primary === undefined) {
                // "the same primary Platform" has no referent without exactly one.
                // `HV-007` and `GV-002` already report that state.
                return MET;
            }
            const range = initiativeInterval(snapshot);
            const audiences = new Set(snapshot.initiative.audienceIds);
            const findings = [];
            for (const candidate of context.overlapCandidates) {
                if (candidate.initiativeId === snapshot.initiative.id) {
                    continue;
                }
                if (candidate.status === 'cancelled') {
                    continue;
                }
                if (candidate.primaryPlatformId !== primary.platformId) {
                    continue;
                }
                if (!dateIntervalsIntersect(range, dateInterval(candidate.startDate, candidate.endDate))) {
                    continue;
                }
                const shared = candidate.audienceIds.filter((audienceId) => audiences.has(audienceId));
                if (shared.length === 0) {
                    continue;
                }
                findings.push({
                    detail: {
                        otherInitiativeId: candidate.initiativeId,
                        otherInitiativeRef: candidate.initiativeRef,
                        primaryPlatformId: primary.platformId,
                        sharedAudienceIds: [...new Set(shared)],
                        otherStartDate: candidate.startDate,
                        otherEndDate: candidate.endDate,
                    },
                });
            }
            return findings.length === 0 ? MET : findings;
        },
    },
    /**
     * A recorded milestone date is earlier than the recorded date of a milestone
     * type that the Master_Data_Administration configures as preceding it (R8.6).
     *
     * The planned date is the recorded date; `completedDate` records what
     * happened, not what was planned, and R10.10's calendar marker reads the
     * planned one. A predecessor type with no recorded date raises nothing: the
     * comparison has no second operand, and the mandatory-milestone governance
     * rule already covers a missing required date.
     *
     * Where the predecessor type carries several recorded dates, the comparison is
     * against the earliest of them: a milestone earlier than even the first
     * recorded occurrence of its predecessor is out of order under any reading,
     * and comparing against the latest would warn about a pair that is in order.
     */
    'WV-002': {
        id: 'WV-002',
        declaredTier: 'warning',
        configurable: true,
        messageKey: 'validation.milestone.outOfOrder',
        fields: [INITIATIVE_FIELD_PATHS.milestones],
        /**
         * The acknowledgement expiry basis of R8.11: the recorded milestone dates by
         * type, which are the values the comparison reads.
         *
         * A set, because the order milestone rows are stored in is not a value the
         * user chose - and structurally equal members are kept rather than collapsed,
         * so deleting one of two identical rows still expires the acknowledgement.
         * The milestone identifier is left out on purpose: re-entering the same type
         * and date as a new row is the same set of recorded dates, and the warning it
         * raises is the one that was acknowledged.
         *
         * `completedDate` is absent because `evaluate` does not read it, and the
         * preceding-type configuration is absent because it is master data rather
         * than a field of this Initiative. R8.11 expires an acknowledgement on the
         * user's "later edit that changes a field value"; an administrator
         * reconfiguring the ordering changes which warnings the next request raises,
         * which R8.5 already handles.
         */
        witness: (snapshot) => ({
            milestones: new Set(snapshot.initiative.milestones.map((milestone) => ({
                milestoneTypeId: milestone.milestoneTypeId,
                plannedDate: milestone.plannedDate,
            }))),
        }),
        evaluate: (snapshot) => {
            const findings = [];
            for (const milestone of snapshot.initiative.milestones) {
                const type = findMasterDataRecord(snapshot.masterData.milestoneTypes, milestone.milestoneTypeId);
                const precedingTypeId = type?.precedingMilestoneTypeId ?? null;
                if (precedingTypeId === null) {
                    continue;
                }
                const precedingDate = earliestRecordedDate(snapshot, precedingTypeId);
                if (precedingDate === undefined || milestone.plannedDate >= precedingDate) {
                    continue;
                }
                findings.push({
                    detail: {
                        milestoneId: milestone.id,
                        milestoneTypeId: milestone.milestoneTypeId,
                        milestoneDate: milestone.plannedDate,
                        precedingMilestoneTypeId: precedingTypeId,
                        precedingMilestoneDate: precedingDate,
                    },
                });
            }
            return findings.length === 0 ? MET : findings;
        },
    },
    // --------------------------------------------------------- information (R8.7)
    /**
     * The applicable master data version, as read-only context (R8.7).
     *
     * Always raised: R8.7 requires the context to be *presented*, not raised on a
     * condition, so this rule has no unmet state. It reports no field, because it
     * is about the record's surroundings rather than a value in it - which is also
     * why it can never block: the gate ignores the information tier.
     */
    'IV-001': {
        id: 'IV-001',
        declaredTier: 'information',
        configurable: false,
        messageKey: 'validation.context.masterDataVersion',
        fields: [],
        evaluate: (_snapshot, context) => [
            { detail: { masterDataVersion: context.masterDataVersion } },
        ],
    },
    /**
     * The last data refresh timestamp, as read-only context (R8.7).
     *
     * Reports the instant this evaluation ran alongside it, because "refreshed at
     * 09:14" is only interpretable next to "as of 09:15" - the pair is what the
     * Detail_Panel's freshness indication (R13.3) shows. Both are excluded from
     * R18.14's determinism guarantee, which names last-data-refresh and generation
     * timestamps explicitly.
     */
    'IV-002': {
        id: 'IV-002',
        declaredTier: 'information',
        configurable: false,
        messageKey: 'validation.context.lastDataRefresh',
        fields: [],
        evaluate: (_snapshot, context) => [
            {
                detail: {
                    lastDataRefreshAt: context.lastDataRefreshAt,
                    evaluatedAt: context.evaluatedAt,
                },
            },
        ],
    },
};
/** Every validation rule, keyed by identifier (R8.1). */
export const VALIDATION_RULES = Object.freeze(VALIDATION_RULE_TABLE);
/** Every registered rule, in {@link VALIDATION_RULE_IDS} order. */
export const VALIDATION_RULE_LIST = Object.freeze(VALIDATION_RULE_IDS.map((id) => VALIDATION_RULES[id]));
/**
 * The rule with that identifier, or `undefined`.
 *
 * `undefined` rather than a throw for the same reason `findMasterDataRecord`
 * returns one: a stored acknowledgement or configuration row naming a rule this
 * release retired is data to ignore, not a crash.
 */
export function findValidationRule(ruleId) {
    return Object.hasOwn(VALIDATION_RULES, ruleId)
        ? VALIDATION_RULES[ruleId]
        : undefined;
}
/**
 * The rules *declared* in a tier.
 *
 * Declared, not effective: a configurable rule's effective tier depends on
 * configuration, so "which rules are governance rules right now" is a question
 * about a {@link ValidationConfiguration} and is answered by reading the tiers of
 * the findings {@link evaluateAll} returns.
 */
export function validationRulesOfDeclaredTier(tier) {
    return VALIDATION_RULE_LIST.filter((rule) => rule.declaredTier === tier);
}
/**
 * Builds the configuration overlay from stored `validation_rule_config` rows.
 *
 * Three kinds of row are dropped rather than applied, and each has a reason:
 *
 * - **A row naming an unknown rule identifier.** A release that no longer has the
 *   rule must not be altered by a leftover row, mirroring how
 *   `capabilitiesForRole` lets an unrecognized role contribute nothing.
 * - **A row naming a non-configurable rule.** R8.9 withholds the hard rules of
 *   R8.3 from configuration, so a row against `HV-003` is inert: it cannot
 *   deactivate the rule, retier it or replace its message. This is the demotion
 *   half of the guarantee whose promotion half is `ConfigurableValidationTier`,
 *   which cannot express `hard` at all.
 * - **A second row for a rule an earlier row already configured.**
 *   `validation_rule_config.rule_id` is the primary key, so a duplicate cannot
 *   come from the database; first-wins keeps the function total for an in-memory
 *   caller that produced one anyway.
 *
 * A message outside 1 to 500 characters is a `RangeError`, not a dropped row. The
 * column's CHECK constraint makes it unreachable from stored data, so reaching it
 * means a caller built the row itself and got it wrong - and quietly falling back
 * to the rule's message key would hide an administrator's message from every user
 * who needed it.
 */
export function validationConfiguration(rows) {
    const overlay = new Map();
    for (const row of rows) {
        if (!isValidRuleMessage(row.message)) {
            throw new RangeError(`Configured message for ${row.ruleId} must be ${String(MIN_RULE_MESSAGE_LENGTH)} to ${String(MAX_RULE_MESSAGE_LENGTH)} characters, received ${String(row.message.length)}`);
        }
        const rule = findValidationRule(row.ruleId);
        if (rule === undefined || !rule.configurable || overlay.has(row.ruleId)) {
            continue;
        }
        overlay.set(row.ruleId, row);
    }
    return overlay;
}
/** The effective settings of every registered rule, in registry order. */
export function effectiveValidationSettings(configuration = EMPTY_VALIDATION_CONFIGURATION) {
    return VALIDATION_RULE_LIST.map((rule) => effectiveRuleSettings(rule, configuration));
}
/** Completes a rule's finding with the identity and effective tier it cannot know. */
function completeFinding(rule, settings, raised) {
    return {
        ruleId: rule.id,
        tier: settings.tier,
        messageKey: raised.messageKey ?? rule.messageKey,
        messageOverride: settings.messageOverride,
        fields: raised.fields ?? rule.fields,
        detail: raised.detail ?? null,
    };
}
/**
 * Evaluates every active rule of all four tiers against one snapshot and returns
 * the complete finding set (R8.12).
 *
 * Exhaustive by construction: the loop runs over the whole registry with no early
 * return, and each rule contributes every finding it raises. The result is
 * therefore the concatenation, in registry order, of the findings of each active
 * rule evaluated independently - which is what Property 20 states and what R5.7's
 * validation summary needs. Nothing here decides what blocks; the gate of task
 * 5.5 does that from the tiers on these findings.
 *
 * Configuration enters at exactly one point: {@link effectiveRuleSettings} per
 * rule. A rule configuration marks inactive is skipped entirely, so its findings
 * are absent rather than present-and-ignored (R8.9); a configured tier lands on
 * every finding of that rule; a configured message rides along as
 * {@link Finding.messageOverride}. None of it touches a rule with
 * `configurable: false`.
 *
 * Pure and free of I/O: the snapshot carries the record and its master data, and
 * {@link RuleContext} carries everything else, including the overlap query result
 * and the evaluation instant. That is what makes the whole set evaluable in one
 * pass within R8.12's 2-second bound.
 *
 * @param snapshot the Initiative aggregate plus the master data the rules resolve
 * @param context what the snapshot cannot carry: the principal, the overlap
 *   candidates, and the context values R8.7 reports
 * @param configuration the `validation_rule_config` overlay, built by
 *   {@link validationConfiguration}; defaults to no configuration at all
 */
export function evaluateAll(snapshot, context, configuration = EMPTY_VALIDATION_CONFIGURATION) {
    const visibility = deriveFieldVisibility(snapshot);
    const findings = [];
    for (const rule of VALIDATION_RULE_LIST) {
        const settings = effectiveRuleSettings(rule, configuration);
        if (!settings.isActive) {
            continue;
        }
        // Skip rules whose fields are ALL hidden (R5.3, Property 23)
        if (rule.fields.length > 0 && rule.fields.every((field) => !isFieldVisible(field, visibility))) {
            continue;
        }
        const raised = rule.evaluate(snapshot, context);
        if (raised === null) {
            continue;
        }
        for (const one of raised) {
            findings.push(completeFinding(rule, settings, one));
        }
    }
    return findings;
}
/**
 * Checks the registry invariants the type system cannot express, throwing on the
 * first breach.
 *
 * Called at server start-up (task 11.5 collects the boot-time assertions), so a
 * registry that drifted fails before it can serve a save. The type system already
 * makes the mapping total over the identifier union and ties each rule's `id` to
 * its key; these are the rest:
 *
 * - **`configurable` agrees with the declared tier.** R8.9 makes exactly the
 *   governance and warning rules configurable. A hard rule marked configurable
 *   would be configurable away, which is the failure R8.9 exists to prevent; a
 *   governance rule marked non-configurable would be silently missing from the
 *   Master_Data_Administration screen.
 * - **Message keys are distinct per rule.** Two rules sharing a default key are
 *   indistinguishable in the validation summary, which R8.2 and R8.4 require to
 *   name the unmet rule.
 * - **A blocking or acknowledgeable rule names at least one field.** R8.2 and
 *   R8.4 require the affected field to be reported. Information findings are
 *   exempt: R8.7's context is about the record's surroundings, not a field in it.
 * - **Every warning rule defines a witness.** R8.11 expires an acknowledgement by
 *   comparing witness hashes, so a warning rule without one would be
 *   acknowledgeable forever - the acknowledgement would survive an edit to the
 *   very values it was given for.
 * - **The R8.3, R8.10, R8.6 and R8.7 memberships hold.** Each cited set is
 *   registered, in the tier the criterion names.
 */
export function assertValidationRegistryConsistent() {
    const seenKeys = new Map();
    for (const rule of VALIDATION_RULE_LIST) {
        const shouldBeConfigurable = rule.declaredTier === 'governance' || rule.declaredTier === 'warning';
        if (rule.configurable !== shouldBeConfigurable) {
            throw new Error(`Validation rule ${rule.id} is declared ${rule.declaredTier} but marked ${rule.configurable ? 'configurable' : 'non-configurable'}; R8.9 makes exactly the governance and warning rules configurable`);
        }
        const owner = seenKeys.get(rule.messageKey);
        if (owner !== undefined) {
            throw new Error(`Validation rules ${owner} and ${rule.id} share the message key ${rule.messageKey}`);
        }
        seenKeys.set(rule.messageKey, rule.id);
        if (rule.declaredTier !== 'information' && rule.fields.length === 0) {
            throw new Error(`Validation rule ${rule.id} names no affected field; R8.2 and R8.4 require one`);
        }
        if (rule.declaredTier === 'warning' && rule.witness === undefined) {
            throw new Error(`Validation rule ${rule.id} is declared warning but defines no witness; R8.11 expires an acknowledgement by comparing witness hashes and has no basis without one`);
        }
    }
    const assertTier = (ruleIds, tier, citation) => {
        for (const ruleId of ruleIds) {
            const rule = VALIDATION_RULES[ruleId];
            if (rule.declaredTier !== tier) {
                throw new Error(`${citation} requires ${ruleId} to be a ${tier} rule, but it is declared ${rule.declaredTier}`);
            }
        }
    };
    assertTier(R8_3_HARD_RULE_IDS, 'hard', 'R8.3');
    assertTier(R8_10_GOVERNANCE_RULE_IDS, 'governance', 'R8.10');
    assertTier(R8_6_WARNING_RULE_IDS, 'warning', 'R8.6');
    assertTier(R8_7_INFORMATION_RULE_IDS, 'information', 'R8.7');
}
/**
 * The recomputed witness hash of every registered rule that defines one (R8.11),
 * ready to be handed to the gate as its witness argument.
 *
 * The counterpart of {@link evaluateAll}: one reads the snapshot for findings, the
 * other reads it for the values those findings' acknowledgements are bound to. A
 * caller that persists an acknowledgement stores the hash from this map under
 * `warning_acknowledgement.witness_hash`, and the next request compares against
 * the map it computes for itself.
 *
 * Deliberately independent of the configuration overlay. A rule configuration
 * deactivated raises no finding, so its hash is never consulted; recomputing over
 * the whole registry keeps the map a function of the snapshot alone, which is what
 * makes a stored hash comparable across requests whose configuration differed.
 */
export function initiativeWitnessHashes(snapshot) {
    return witnessHashesOf(VALIDATION_RULE_LIST, snapshot);
}
//# sourceMappingURL=validation-registry.js.map